import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Select, Space, treeSelectOptions, useLocale } from "@app/ui";
import { useAuth } from "../auth/AuthContext.js";
import { useT } from "../i18n.js";
import { CreateOrgModal, useCanCreateOrg } from "./CreateOrgModal.js";
import { useOrgs } from "./OrgContext.js";

/**
 * Header org switcher: a compact Select over the user's orgs plus a
 * "create organization" trigger (shown when the user can provision an org — a
 * platform admin, a self-service user, or an admin of the selected org who can
 * open a sub-org). The form itself lives in the shared `CreateOrgModal`.
 */
export function OrgSwitcher() {
  const t = useT();
  const { locale } = useLocale();
  const { user } = useAuth();
  const { orgs, org, selectOrg } = useOrgs();
  const canCreateOrg = useCanCreateOrg();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  // An admin of the selected org can always open a sub-org, even where top-level
  // self-service is off; either path reveals the "+".
  const canOpen = canCreateOrg || org?.memberRole === "admin";

  // The API returns a flat list ordered by name, which scatters companies away
  // from their holding. Re-order it as a tree so each company sits under its
  // parent — indented, and labelled with the parent name so the grouping
  // survives a search (which re-sorts rows by match score).
  const options = useMemo(
    () =>
      treeSelectOptions(
        orgs.map((o) => ({ id: o.id, parentId: o.parentId, label: o.name, searchText: o.slug })),
        locale,
      ),
    [orgs, locale],
  );

  return (
    <Space size="sm" className="min-w-0">
      {/* min-w-0 + a width basis (not a hard min-width) so the switcher fills
          ~190px on desktop but shrinks on a narrow header — the Select label
          truncates instead of forcing the whole header to overflow the viewport. */}
      {orgs.length > 0 && (
        <div className="min-w-0" style={{ width: 190, maxWidth: "100%" }}>
          <Select
            size="sm"
            options={options}
            searchPlaceholder={t("org.search")}
            value={org?.id ?? ""}
            onChange={(id) => {
              if (id === org?.id) return;
              selectOrg(id);
              // Whatever was on screen belonged to the previous tenant — reset home.
              navigate("/dashboard");
            }}
          />
        </div>
      )}
      {user && canOpen && (
        <Button
          size="sm"
          variant="subtle"
          icon={<Icon name="plus" size={14} />}
          aria-label={t("org.create")}
          onClick={() => setOpen(true)}
        />
      )}

      <CreateOrgModal open={open} onClose={() => setOpen(false)} />
    </Space>
  );
}
